<template>
    <!-- Campo con etiqueta flotante, mismo diseño que los del formulario nuevo
         (/formulario). El error se muestra solo cuando la vista lo pide
         (`error` con texto): la vista decide cuándo (al salir del campo o al
         intentar guardar). -->
    <div :class="['bk-field', { 'is-invalid': !!error, 'is-filled': filled, 'is-disabled': disabled, 'is-fijo': fijo, 'has-accion': !!$slots.accion }]">
        <div :class="['bk-control', { 'bk-control--area': textarea }]">
            <textarea
                v-if="textarea"
                :id="id"
                :value="value"
                :maxlength="maxlength || null"
                :aria-invalid="error ? 'true' : 'false'"
                :aria-describedby="describedBy"
                placeholder=" "
                rows="4"
                @input="$emit('input', $event.target.value)"
                @blur="$emit('blur')"
            />
            <input
                v-else
                :id="id"
                :value="value"
                :type="type"
                :inputmode="inputmode || null"
                :autocomplete="autocomplete || 'off'"
                :maxlength="maxlength || null"
                :disabled="disabled"
                :required="!optional"
                :aria-required="optional ? 'false' : 'true'"
                :aria-invalid="error ? 'true' : 'false'"
                :aria-describedby="describedBy"
                placeholder=" "
                @input="$emit('input', $event.target.value)"
                @blur="$emit('blur')"
            />
            <label :for="id">
                {{ label }}<span v-if="optional" class="bk-opt"> · {{ optionalText }}</span>
            </label>
            <span v-if="ok && !error && type !== 'date'" class="bk-tick" aria-hidden="true">✓</span>
            <!-- Botón dentro del campo, a la derecha (p. ej. el ojo de mostrar la contraseña). -->
            <span v-if="$slots.accion" class="bk-accion"><slot name="accion" /></span>
        </div>

        <div class="bk-foot">
            <p :id="`${id}-error`" class="bk-error" role="alert">{{ error }}</p>
            <p v-if="hint && !error" :id="`${id}-hint`" class="bk-hint">{{ hint }}</p>
            <span v-if="maxlength && textarea" class="bk-counter">{{ (value || '').length }}/{{ maxlength }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BkField',
    props: {
        id: { type: String, required: true },
        value: { type: String, default: '' },
        label: { type: String, required: true },
        hint: { type: String, default: '' },
        error: { type: String, default: '' },
        optional: { type: Boolean, default: false },
        optionalText: { type: String, default: '' },
        // El campo está bien completo: muestra el ✓ (solo si tiene texto).
        ok: { type: Boolean, default: false },
        type: { type: String, default: 'text' },
        inputmode: { type: String, default: '' },
        autocomplete: { type: String, default: '' },
        maxlength: { type: Number, default: 0 },
        textarea: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        // Etiqueta siempre arriba (p. ej. type=date, que muestra dd/mm/aaaa).
        fijo: { type: Boolean, default: false },
    },
    computed: {
        filled() {
            return !!(this.value && String(this.value).length);
        },
        describedBy() {
            if (this.error) return `${this.id}-error`;
            return this.hint ? `${this.id}-hint` : null;
        },
    },
};
</script>

<style scoped>
.bk-field { position: relative; min-width: 0; margin: 0 0 18px; }

.bk-control { position: relative; }
.bk-control input,
.bk-control textarea {
    display: block;
    width: 100%;
    height: 60px;
    margin: 0;
    padding: 24px 40px 8px 16px;
    border: 1.5px solid var(--bk-line-2);
    border-radius: var(--bk-radius);
    background: #fff;
    font: inherit;
    font-size: 16px;
    color: var(--bk-ink);
    outline: none;
    box-shadow: none;
    -webkit-appearance: none;
    appearance: none;
    transition: border-color .2s, box-shadow .2s, background .2s;
}
.bk-control textarea { height: auto; min-height: 120px; padding-top: 30px; resize: vertical; line-height: 1.55; }

.bk-control label {
    position: absolute;
    left: 17px;
    top: 30px;
    margin: 0;
    transform: translateY(-50%);
    font-size: 16px;
    font-weight: 400;
    color: var(--bk-muted);
    pointer-events: none;
    white-space: nowrap;
    max-width: calc(100% - 50px);
    overflow: hidden;
    text-overflow: ellipsis;
    transition: top .2s var(--bk-ease), font-size .2s var(--bk-ease), color .2s;
}
.bk-opt { color: #9a918a; font-weight: 400; }

.bk-control input:hover, .bk-control textarea:hover { border-color: #cbbcae; }
.bk-control input:focus, .bk-control textarea:focus {
    border-color: var(--bk-orange);
    box-shadow: var(--bk-ring);
    background: #fffdfb;
}
.bk-control input:focus + label,
.bk-control input:not(:placeholder-shown) + label,
.bk-control textarea:focus + label,
.bk-control textarea:not(:placeholder-shown) + label { top: 18px; font-size: 12px; font-weight: 500; }
.bk-control input:focus + label, .bk-control textarea:focus + label { color: var(--bk-orange-dark); }
.bk-control--area label { top: 26px; }
.is-fijo .bk-control label { top: 18px; font-size: 12px; font-weight: 500; }
.is-fijo .bk-control input { padding-right: 14px; }
.bk-control input[type=date]::-webkit-calendar-picker-indicator { cursor: pointer; opacity: .55; }
/* Con botón adentro (slot "accion"): el texto no pasa por debajo del botón. */
.has-accion .bk-control input { padding-right: 60px; }
.has-accion .bk-tick { right: 54px; }
.bk-accion { position: absolute; right: 8px; top: 30px; transform: translateY(-50%); display: flex; }
.bk-control input[type=password] { letter-spacing: .14em; }
.is-disabled .bk-control input { background: #f4f3f0; color: #6f6760; cursor: not-allowed; border-style: dashed; }

.bk-tick {
    position: absolute;
    right: 14px;
    top: 30px;
    transform: translateY(-50%);
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--bk-green-bg);
    color: var(--bk-green);
    font-size: 12px;
    font-weight: 700;
    animation: bk-pop .3s var(--bk-ease-back) both;
}

/* Error */
.is-invalid .bk-control input,
.is-invalid .bk-control textarea { border-color: var(--bk-danger); background: #fffafa; }
.is-invalid .bk-control input:focus,
.is-invalid .bk-control textarea:focus { box-shadow: 0 0 0 4px rgba(217, 58, 43, .14); }
.is-invalid .bk-control label { color: var(--bk-danger); }

.bk-foot { display: flex; justify-content: flex-start; align-items: flex-start; gap: 12px; }
.bk-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--bk-danger);
    transition: max-height .3s var(--bk-ease), opacity .25s, margin .3s var(--bk-ease);
}
.is-invalid .bk-error { max-height: 60px; opacity: 1; margin-top: 7px; }
/* Sin error no ocupa lugar: la ayuda queda alineada a la izquierda. */
.bk-field:not(.is-invalid) .bk-error { display: none; }
.bk-error::before {
    content: '!';
    flex: none;
    display: grid;
    place-items: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--bk-danger);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
}
.bk-hint { margin: 7px 2px 0; font-size: 13px; color: var(--bk-muted); }
.bk-counter { margin-top: 7px; margin-left: auto; font-size: 12px; color: var(--bk-muted); font-variant-numeric: tabular-nums; }

@keyframes bk-pop { from { opacity: 0; transform: translateY(-50%) scale(.4); } to { opacity: 1; transform: translateY(-50%) scale(1); } }
</style>
